import React from "react";
import {
  CalendarDays,
  Target as TargetIcon,
  NotebookPen,
  MessageSquareText,
  Watch,
  ArrowUpRight,
} from "lucide-react";

const FEATURES = [
  { icon: CalendarDays, label: "Daily workouts" },
  { icon: TargetIcon, label: "Training targets" },
  { icon: NotebookPen, label: "Session notes" },
  { icon: MessageSquareText, label: "Workout feedback" },
];

const FINAL_SURGE_URL = "https://www.finalsurge.com";

export default function Platform() {
  return (
    <section
      id="platform"
      data-testid="platform-section"
      className="relative py-24 lg:py-32 border-b border-white/10 bg-[#080808] overflow-hidden"
    >
      <div className="absolute inset-0 tech-grid opacity-25 pointer-events-none" />
      {/* Final Surge screenshot — subtle background behind the header */}
      <div className="pointer-events-none absolute top-0 left-0 right-0 h-[680px] lg:h-[640px] overflow-hidden">
        <img
          src="/programs/final-surge-banner.jpg"
          alt=""
          aria-hidden="true"
          className="absolute top-[60px] right-[-2%] w-[80%] lg:w-[62%] max-w-[1100px] opacity-[0.32] contrast-[1.05] [mask-image:radial-gradient(ellipse_at_top_right,black_35%,transparent_75%)]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-[#080808]" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12">
          <div className="lg:col-span-3 flex items-start gap-4">
            <span className="font-mono text-brand-red text-xs tracking-widest">04</span>
            <span className="label-eyebrow">The Platform</span>
          </div>
          <div className="lg:col-span-9">
            <h2 className="font-display uppercase text-white text-5xl lg:text-7xl leading-[0.9] tracking-tight max-w-3xl">
              Trained on{" "}
              <a
                href={FINAL_SURGE_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="platform-finalsurge-link"
                className="text-brand-red underline decoration-brand-red/40 underline-offset-[10px] decoration-2 hover:decoration-brand-red transition-colors"
              >
                Final Surge.
              </a>
            </h2>
            <p className="mt-6 max-w-2xl text-white/65 text-base lg:text-lg leading-relaxed">
              All training plans are delivered on the{" "}
              <a
                href={FINAL_SURGE_URL}
                target="_blank"
                rel="noreferrer"
                className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-brand-red hover:text-brand-red transition-colors"
              >
                Final Surge
              </a>{" "}
              training platform — available on web and mobile.
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Calendar feature card */}
          <article
            data-testid="platform-calendar-card"
            className="lg:col-span-7 bg-[#0e0e10] border border-white/10 p-8 lg:p-12"
          >
            <div className="flex items-center gap-3 mb-8">
              <CalendarDays size={18} className="text-brand-red" />
              <span className="label-eyebrow text-white/60">Athlete Calendar</span>
            </div>
            <h3 className="font-display uppercase text-white text-3xl lg:text-5xl leading-[0.95] tracking-tight max-w-md">
              Your week, mapped out.
            </h3>
            <p className="mt-5 text-white/65 leading-relaxed max-w-md">
              Your training schedule is uploaded directly to your athlete calendar so you
              always know exactly what's on for the day.
            </p>

            <ul className="mt-10 grid grid-cols-2 gap-4">
              {FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <li
                    key={i}
                    data-testid={`platform-feature-${i}`}
                    className="flex items-center gap-3 border border-white/10 px-4 py-3 hover:border-brand-red/50 hover:bg-white/[0.02] transition-colors"
                  >
                    <Icon size={16} className="text-brand-red shrink-0" />
                    <span className="text-sm text-white/85">{f.label}</span>
                  </li>
                );
              })}
            </ul>
          </article>

          {/* Watch sync card */}
          <article
            data-testid="platform-watch-card"
            className="lg:col-span-5 bg-[#0e0e10] border border-white/10 p-8 lg:p-12 flex flex-col"
          >
            <div className="flex items-center gap-3 mb-8">
              <Watch size={18} className="text-brand-red" />
              <span className="label-eyebrow text-white/60">Device Sync</span>
            </div>
            <h3 className="font-display uppercase text-white text-3xl lg:text-5xl leading-[0.95] tracking-tight">
              Straight to your watch.
            </h3>
            <p className="mt-5 text-white/65 leading-relaxed">
              Final Surge integrates seamlessly with your running devices. I build workouts
              and send them directly to your watch — all you have to do is press start.
            </p>

            <div className="mt-auto pt-10">
              <a
                href={FINAL_SURGE_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="platform-cta"
                className="inline-flex items-center gap-2 text-white border-b border-white/20 hover:border-brand-red hover:text-brand-red transition-colors pb-1 text-sm uppercase tracking-[0.18em] font-bold"
              >
                Visit Final Surge <ArrowUpRight size={16} />
              </a>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
